import { utcToZonedTime } from 'date-fns-tz';
import { createEvent } from 'ics';
import { documentToHtmlString } from '@contentful/rich-text-html-renderer';
import {
  Appointment,
  AppointmentsByTime,
  AppointmentTypes,
} from '../schema.design';

type ContentfulRichTextJson = null | object;
export type CalEvent = {
  title: string;
  timeZone: string;
  description?: ContentfulRichTextJson;
  collectionDescription: ContentfulRichTextJson;
  inspectionDescription: ContentfulRichTextJson;
  location: string;
  organizer?: {
    name: string;
    email: string;
  } | null;
};

const formatDateTime = (d: Date): [number, number, number, number, number] => [
  d.getFullYear(),
  d.getMonth() + 1,
  d.getDate(),
  d.getHours(),
  d.getMinutes(),
];

export function downloadEvent({
  title,
  startDateTime,
  endDateTime,
  description,
  location,
  organizer,
}) {
  const { value: ics } = createEvent({
    start: formatDateTime(startDateTime),
    end: formatDateTime(endDateTime),
    startOutputType: 'local',
    title,
    htmlContent: documentToHtmlString(description),
    location,
    url: window.location.href,
    organizer,
  });

  if (!ics) {
    console.error('Failed to generate ICS file');
    return;
  }

  const blob = new Blob([ics], { type: 'text/calendar' });

  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `${title.replace(/\s+/g, '_')}.ics`;
  document.body.appendChild(link);
  link.click();

  document.body.removeChild(link);
}

export function sortByEnded(
  appointments: Appointment[],
  timeZone = 'Australia/Sydney'
): AppointmentsByTime {
  const toLocal = (dateTime: string | Date) =>
    utcToZonedTime(new Date(dateTime), timeZone);

  const now = toLocal(new Date());

  const sortedAppointments = appointments
    .sort((a, b) => {
      const aEnded = toLocal(a.endDateTime) < now;
      const bEnded = toLocal(b.endDateTime) < now;
      if (aEnded === bEnded) return 0;
      return aEnded ? 1 : -1;
    })
    .reduce(
      (a: AppointmentsByTime, c: Appointment) => {
        if (toLocal(c.endDateTime) < now) {
          a.past.push(c);
        } else {
          a.current.push(c);
        }
        return a;
      },
      { past: [], current: [] }
    );
  return sortedAppointments;
}

export function sortByType(
  appointments: Appointment[]
): [Appointment[], Appointment[]] {
  return appointments.reduce(
    (a: ReturnType<typeof sortByType>, c) => {
      if (c.type === AppointmentTypes.Inspection) {
        a[0].push(c);
      }
      if (c.type === AppointmentTypes.Collection) {
        a[1].push(c);
      }
      return a;
    },
    [[], []]
  );
}
